import { useState, useEffect, useCallback } from 'react';
import { encryptData, decryptData } from '../utils/encryption';

/**
 * Custom hook to manage shared state between calculators
 * Synchronizes common fields between website and branding calculators
 *
 * @param {string} paramName - URL parameter name for this calculator
 * @param {Object} defaultValue - Default state value
 * @param {Array} sharedFields - Array of field names that should be shared
 * @param {Function} onSharedUpdate - Callback when shared values are updated from other calculator
 * @returns {[Object, Function]} - Current state and update function
 */
const useSharedState = (paramName, defaultValue, sharedFields = [], onSharedUpdate = () => {}) => {
  // Initialize state with values from URL or defaults
  const [state, setState] = useState(() => {
    try {
      // First check if we have any persisted state in localStorage
      const persistedState = localStorage.getItem(`calculator_${paramName}`);
      if (persistedState) {
        try {
          const parsedState = JSON.parse(persistedState);
          return parsedState;
        } catch (e) {
          // Invalid JSON in localStorage, ignore it
          console.error('Invalid JSON in localStorage', e);
        }
      }

      // Check URL parameters as a fallback
      const hash = window.location.hash.substring(1);
      const hashParts = hash.split('?');
      const queryParams = hashParts.length > 1 ? new URLSearchParams(hashParts[1]) : new URLSearchParams();

      // First try to get our specific parameter
      if (queryParams.has(paramName)) {
        const encryptedData = queryParams.get(paramName);
        const decryptedData = decryptData(encryptedData);
        if (decryptedData) {
          // Store this data in localStorage for persistence
          localStorage.setItem(`calculator_${paramName}`, JSON.stringify(decryptedData));
          return decryptedData;
        }
      }

      // If we didn't find our parameter but found the other calculator's parameter
      const otherParamName = paramName === 'brandData' ? 'data' : 'brandData';
      if (queryParams.has(otherParamName)) {
        const encryptedData = queryParams.get(otherParamName);
        const otherData = decryptData(encryptedData);
        if (otherData) {
          // Extract shared values from other calculator
          const sharedValues = {};
          sharedFields.forEach(field => {
            if (otherData[field] !== undefined) {
              sharedValues[field] = otherData[field];
            }
          });

          // Merge shared values with our defaults
          const mergedData = { ...defaultValue, ...sharedValues };
          
          // Store this data in localStorage for persistence
          localStorage.setItem(`calculator_${paramName}`, JSON.stringify(mergedData));
          return mergedData;
        }
      }

      // Check if we have shared values in localStorage
      const sharedStateStr = localStorage.getItem('calculatorSharedState');
      if (sharedStateStr) {
        try {
          const sharedState = JSON.parse(sharedStateStr);
          
          // Extract shared values
          const sharedValues = {};
          sharedFields.forEach(field => {
            if (sharedState[field] !== undefined) {
              sharedValues[field] = sharedState[field];
            }
          });
          
          if (Object.keys(sharedValues).length > 0) {
            const mergedData = { ...defaultValue, ...sharedValues };
            
            // Store this data in localStorage for persistence
            localStorage.setItem(`calculator_${paramName}`, JSON.stringify(mergedData));
            return mergedData;
          }
        } catch (e) {
          // Invalid JSON in localStorage, ignore it
          console.error('Invalid shared state JSON in localStorage', e);
        }
      }

      // If nothing else worked, return default values
      return defaultValue;
    } catch (error) {
      console.error('Error initializing shared state:', error);
      return defaultValue;
    }
  });

  // Listen for storage events to sync between tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'calculatorSharedState') {
        try {
          const sharedState = JSON.parse(e.newValue || '{}');
          const updatedValues = {};
          let hasUpdates = false;
          
          // Only update the shared fields
          sharedFields.forEach(field => {
            if (sharedState[field] !== undefined && sharedState[field] !== state[field]) {
              updatedValues[field] = sharedState[field];
              hasUpdates = true;
            }
          });
          
          if (hasUpdates) {
            setState(prevState => {
              const newState = { ...prevState, ...updatedValues };
              
              // Update our persisted state
              localStorage.setItem(`calculator_${paramName}`, JSON.stringify(newState));
              return newState;
            });
            onSharedUpdate(updatedValues);
          }
        } catch (error) {
          console.error('Error processing storage event:', error);
        }
      }

      // Listen for direct updates to our calculator state
      if (e.key === `calculator_${paramName}`) {
        try {
          const newState = JSON.parse(e.newValue || '{}');
          setState(newState);
        } catch (error) {
          console.error('Error processing calculator state update:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [state, paramName, sharedFields, onSharedUpdate]);

  // Update shared state and URL when state changes
  useEffect(() => {
    // Save shared values to localStorage for cross-tab sync
    const sharedValues = {};
    sharedFields.forEach(field => {
      if (state[field] !== undefined) {
        sharedValues[field] = state[field];
      }
    });
    
    if (Object.keys(sharedValues).length > 0) {
      localStorage.setItem('calculatorSharedState', JSON.stringify(sharedValues));
      
      // Update the other calculator's state as well
      const otherParamName = paramName === 'brandData' ? 'data' : 'brandData';
      try {
        const otherStateStr = localStorage.getItem(`calculator_${otherParamName}`);
        if (otherStateStr) {
          const otherState = JSON.parse(otherStateStr);
          const updatedOtherState = { ...otherState };
          let hasChanges = false;
          
          // Update shared fields in the other calculator
          sharedFields.forEach(field => {
            if (sharedValues[field] !== undefined && sharedValues[field] !== otherState[field]) {
              updatedOtherState[field] = sharedValues[field];
              hasChanges = true;
            }
          });
          
          if (hasChanges) {
            localStorage.setItem(`calculator_${otherParamName}`, JSON.stringify(updatedOtherState));
            
            // Dispatch a storage event to notify other tabs
            window.dispatchEvent(new StorageEvent('storage', {
              key: `calculator_${otherParamName}`,
              newValue: JSON.stringify(updatedOtherState)
            }));
          }
        }
      } catch (error) {
        console.error('Error updating other calculator state:', error);
      }
    }

    // Also save the full state for this specific calculator
    localStorage.setItem(`calculator_${paramName}`, JSON.stringify(state));

    // Update URL parameters
    const encryptedData = encryptData(state);
    
    // Get current hash and extract parts
    const hash = window.location.hash.substring(1);
    let [route, queryString] = hash.split('?');
    
    // If there's no explicit route but there's a query string
    if (!route && queryString) {
      route = '';
    }
    
    // Parse existing query parameters
    const params = new URLSearchParams(queryString || '');
    
    // Update our parameter
    params.set(paramName, encryptedData);
    
    // Rebuild the hash
    const newHash = route ? `${route}?${params.toString()}` : `?${params.toString()}`;
    
    // Update URL without triggering navigation
    window.history.replaceState(
      null,
      document.title,
      window.location.pathname + '#' + newHash
    );
  }, [state, paramName, sharedFields]);

  // Update state function
  const updateState = useCallback((newState) => {
    if (typeof newState === 'function') {
      setState(prevState => {
        const updatedState = newState(prevState);
        
        // Extract shared values that were updated
        const sharedValues = {};
        sharedFields.forEach(field => {
          if (updatedState[field] !== prevState[field]) {
            sharedValues[field] = updatedState[field];
          }
        });
        
        // If we have shared values that changed, save them to localStorage
        if (Object.keys(sharedValues).length > 0) {
          try {
            const existingShared = JSON.parse(localStorage.getItem('calculatorSharedState') || '{}');
            localStorage.setItem('calculatorSharedState', JSON.stringify({...existingShared, ...sharedValues}));
            
            // Update the other calculator's state immediately
            const otherParamName = paramName === 'brandData' ? 'data' : 'brandData';
            try {
              const otherStateStr = localStorage.getItem(`calculator_${otherParamName}`);
              if (otherStateStr) {
                const otherState = JSON.parse(otherStateStr);
                const updatedOtherState = { ...otherState };
                let hasChanges = false;
                
                // Update shared fields in the other calculator
                sharedFields.forEach(field => {
                  if (sharedValues[field] !== undefined && sharedValues[field] !== otherState[field]) {
                    updatedOtherState[field] = sharedValues[field];
                    hasChanges = true;
                  }
                });
                
                if (hasChanges) {
                  localStorage.setItem(`calculator_${otherParamName}`, JSON.stringify(updatedOtherState));
                  
                  // Dispatch a storage event to notify other components
                  window.dispatchEvent(new StorageEvent('storage', {
                    key: `calculator_${otherParamName}`,
                    newValue: JSON.stringify(updatedOtherState)
                  }));
                }
              }
            } catch (error) {
              console.error('Error updating other calculator state:', error);
            }
          } catch (e) {
            localStorage.setItem('calculatorSharedState', JSON.stringify(sharedValues));
          }
        }
        
        // Update our persisted state
        localStorage.setItem(`calculator_${paramName}`, JSON.stringify(updatedState));
        return updatedState;
      });
    } else {
      setState(prevState => {
        // Extract shared values that were updated
        const sharedValues = {};
        sharedFields.forEach(field => {
          if (newState[field] !== undefined && newState[field] !== prevState[field]) {
            sharedValues[field] = newState[field];
          }
        });
        
        // If we have shared values that changed, save them to localStorage
        if (Object.keys(sharedValues).length > 0) {
          try {
            const existingShared = JSON.parse(localStorage.getItem('calculatorSharedState') || '{}');
            localStorage.setItem('calculatorSharedState', JSON.stringify({...existingShared, ...sharedValues}));
            
            // Update the other calculator's state immediately
            const otherParamName = paramName === 'brandData' ? 'data' : 'brandData';
            try {
              const otherStateStr = localStorage.getItem(`calculator_${otherParamName}`);
              if (otherStateStr) {
                const otherState = JSON.parse(otherStateStr);
                const updatedOtherState = { ...otherState };
                let hasChanges = false;
                
                // Update shared fields in the other calculator
                sharedFields.forEach(field => {
                  if (sharedValues[field] !== undefined && sharedValues[field] !== otherState[field]) {
                    updatedOtherState[field] = sharedValues[field];
                    hasChanges = true;
                  }
                });
                
                if (hasChanges) {
                  localStorage.setItem(`calculator_${otherParamName}`, JSON.stringify(updatedOtherState));
                  
                  // Dispatch a storage event to notify other components
                  window.dispatchEvent(new StorageEvent('storage', {
                    key: `calculator_${otherParamName}`,
                    newValue: JSON.stringify(updatedOtherState)
                  }));
                }
              }
            } catch (error) {
              console.error('Error updating other calculator state:', error);
            }
          } catch (e) {
            localStorage.setItem('calculatorSharedState', JSON.stringify(sharedValues));
          }
        }
        
        const updatedState = { ...prevState, ...newState };
        
        // Update our persisted state
        localStorage.setItem(`calculator_${paramName}`, JSON.stringify(updatedState));
        return updatedState;
      });
    }
  }, [sharedFields, paramName]);

  return [state, updateState];
};

export default useSharedState;